<div class="instructions-page">
  <div class="instructions-page__hero">
    <h1 class="instructions-page__title">Operator Guide</h1>
    <p class="instructions-page__lead">
      Everything you need to manage orders, assign drivers, and keep the fleet running smoothly.
    </p>
  </div>

  <div class="instructions-page__grid">

    <!-- Layout Overview -->
    <section class="instructions-card instructions-card--wide">
      <h2 class="instructions-card__title">
        <i class="fa fa-th-large" aria-hidden="true"></i>
        Dashboard Layout
      </h2>
      <p class="instructions-card__desc">
        The screen is split into two resizable panels. Drag the divider between them to adjust.
      </p>
      <ul class="instructions-list">
        <li><strong>Left panel — Workflow</strong>: order lists, tabs, search filters, and the selected order detail.</li>
        <li><strong>Right panel — Map</strong>: live driver and order positions. Clicking anything on the map selects it in the left panel too.</li>
        <li><strong>Header</strong>: connection status, driver counts, search inputs, and sound toggles.</li>
      </ul>
    </section>

    <!-- Connection Status -->
    <section class="instructions-card">
      <h2 class="instructions-card__title">
        <i class="fa fa-wifi" aria-hidden="true"></i>
        Connection Status
      </h2>
      <p class="instructions-card__desc">
        The colored dot in the header shows whether live data is flowing.
      </p>
      <ul class="instructions-status-list">
        <li>
          <span class="wm-conn-dot wm-conn-dot--online"></span>
          <strong>ONLINE</strong> — Live data is streaming normally.
        </li>
        <li>
          <span class="wm-conn-dot wm-conn-dot--reconnecting"></span>
          <strong>RECONNECTING</strong> — Connection lost; retrying automatically. Wait a moment.
        </li>
        <li>
          <span class="wm-conn-dot wm-conn-dot--error"></span>
          <strong>ERROR</strong> — Failed after several attempts. Check your network or refresh.
        </li>
        <li>
          <span class="wm-conn-dot wm-conn-dot--offline"></span>
          <strong>OFFLINE</strong> — Not connected. Refresh the page.
        </li>
      </ul>
      <p class="instructions-card__note">
        A debug panel appears automatically under the badge when reconnecting or in error state — it shows the socket URL and retry count.
      </p>
    </section>

    <!-- Driver Counts -->
    <section class="instructions-card">
      <h2 class="instructions-card__title">
        <i class="fa fa-car" aria-hidden="true"></i>
        Live Driver Counts
      </h2>
      <p class="instructions-card__desc">
        Three numbers in the header update every 30 seconds.
      </p>
      <ul class="instructions-status-list">
        <li>
          <i class="fa fa-circle" style="color:var(--wm-success);font-size:10px;" aria-hidden="true"></i>
          <strong>Green</strong> — Free drivers available to take orders.
        </li>
        <li>
          <i class="fa fa-circle" style="color:var(--wm-danger);font-size:10px;" aria-hidden="true"></i>
          <strong>Red</strong> — Busy drivers currently on a trip.
        </li>
        <li>
          <i class="fa fa-circle" style="color:var(--wm-accent);font-size:10px;" aria-hidden="true"></i>
          <strong>Blue</strong> — All online drivers combined.
        </li>
      </ul>
    </section>

    <!-- Tabs -->
    <section class="instructions-card">
      <h2 class="instructions-card__title">
        <i class="fa fa-list" aria-hidden="true"></i>
        Order Tabs
      </h2>
      <p class="instructions-card__desc">
        Use the tabs at the top of the left panel to switch between order views.
      </p>
      <dl class="instructions-dl">
        <dt><span class="wm-chip wm-chip-warning">Pending</span></dt>
        <dd>Orders waiting to be assigned to a driver. This is where most of your attention goes.</dd>
        <dt><span class="wm-chip wm-chip-info">Preorder</span></dt>
        <dd>Scheduled future orders (shuttles and lines). The badge shows the unprocessed count. Orders approaching dispatch time trigger an alarm — see <strong>Preorder Alarm</strong> below.</dd>
        <dt><span class="wm-chip wm-chip-info">ggAra</span></dt>
        <dd>ggAra shuttle preorders. Same structure and alarm rules as the Preorder tab.</dd>
        <dt><span class="wm-chip wm-chip-success">Active</span></dt>
        <dd>Orders currently in progress with an assigned driver.</dd>
        <dt><span class="wm-chip">All</span></dt>
        <dd>Side-by-side view of Pending and Active orders for full visibility at once.</dd>
        <dt><span class="wm-chip wm-chip-danger">Balance</span></dt>
        <dd>Partners with a negative account balance. This tab only appears when at least one partner owes money — a danger indicator shows in the tab label.</dd>
      </dl>
    </section>

    <!-- Queue Summary -->
    <section class="instructions-card">
      <h2 class="instructions-card__title">
        <i class="fa fa-bar-chart" aria-hidden="true"></i>
        Queue Summary
      </h2>
      <p class="instructions-card__desc">
        Five count chips in the top-right of the workflow panel update in real time.
      </p>
      <ul class="instructions-status-list">
        <li><span class="wm-chip wm-chip-warning">5</span> <strong>Pending</strong> — awaiting driver.</li>
        <li><span class="wm-chip wm-chip-info">22</span> <strong>Accepted</strong> — driver assigned, heading to pickup.</li>
        <li><span class="wm-chip wm-chip-success">4</span> <strong>Waiting</strong> — driver at pickup, waiting for passenger.</li>
        <li><span class="wm-chip">56</span> <strong>Processing</strong> — trip in progress.</li>
        <li><span class="wm-chip wm-chip-accent">87</span> <strong>Total</strong> — all active orders combined.</li>
      </ul>
    </section>

    <!-- Order Row Colors -->
    <section class="instructions-card">
      <h2 class="instructions-card__title">
        <i class="fa fa-paint-brush" aria-hidden="true"></i>
        Order Row Colors
      </h2>
      <p class="instructions-card__desc">
        Each row's background tells you the current order state at a glance.
      </p>
      <ul class="instructions-status-list">
        <li><span class="instructions-color-strip" style="background:#ffffff;border:1px solid var(--wm-border-strong);"></span> <strong>White</strong> — New order, no driver assigned yet.</li>
        <li><span class="instructions-color-strip" style="background:#23b7e5;"></span> <strong>Blue</strong> — Driver already assigned.</li>
        <li><span class="instructions-color-strip" style="background:#fad733;"></span> <strong>Yellow</strong> — Order is being processed / assigned.</li>
        <li><span class="instructions-color-strip" style="background:#7266ba;"></span> <strong>Purple</strong> — Priority car type.</li>
        <li><span class="instructions-color-strip" style="background:#f05050;"></span> <strong>Red</strong> — Cancelled.</li>
        <li><span class="instructions-color-strip" style="background:#fff1b0;"></span> <strong>Pale yellow</strong> — Active, driver accepted and on the way.</li>
        <li><span class="instructions-color-strip" style="background:#aaf7bd;"></span> <strong>Pale green</strong> — Driver waiting at pickup.</li>
      </ul>
    </section>

    <!-- Order Badges -->
    <section class="instructions-card">
      <h2 class="instructions-card__title">
        <i class="fa fa-tags" aria-hidden="true"></i>
        Order Badges
      </h2>
      <p class="instructions-card__desc">
        Small tags in the order ID cell give extra context about the customer or order.
      </p>
      <ul class="instructions-status-list">
        <li><span class="wm-chip wm-chip-success" style="font-size:10px;">NEW</span> First-time customer — 0 completed trips in the last 6 months.</li>
        <li><span class="wm-chip wm-chip-vip" style="font-size:10px;">★ VIP</span> Loyal customer — 150+ completed trips in the last 6 months.</li>
        <li><span class="wm-chip wm-chip-warning" style="font-size:10px;">3</span> Number of cancellations by this customer. Pay attention if the number is high.</li>
      </ul>
      <p class="instructions-card__desc" style="margin-top:10px;">
        Type badges in the second column describe the order category (car type, business, corporate, delivery, etc.).
      </p>
    </section>

    <!-- Selecting an Order -->
    <section class="instructions-card">
      <h2 class="instructions-card__title">
        <i class="fa fa-hand-pointer-o" aria-hidden="true"></i>
        Selecting an Order
      </h2>
      <p class="instructions-card__desc">
        Click any order row in the list <em>or</em> click its circle on the map. Both open the same detail panel above the list.
      </p>
      <p class="instructions-card__desc">
        The detail panel shows:
      </p>
      <ul class="instructions-list">
        <li><strong>Order ID</strong> — click to copy.</li>
        <li><strong>Status &amp; badges</strong> — current state, surge price, corporate/business flags, VIP/NEW/cancel count.</li>
        <li><strong>Elapsed time</strong> — how long since the order was placed.</li>
        <li><strong>Pickup address</strong> and comment.</li>
        <li><strong>Destination address</strong> and comment (if set).</li>
        <li><strong>Driver details</strong> — name, car, phone (click to copy), with a CRM link.</li>
        <li><strong>Customer phone</strong> — click to copy, with a CRM link.</li>
        <li><strong>Order history</strong> — trips in the last 6 months, cancellation count, estimated fare.</li>
        <li><strong>Stops</strong> — editable list for multi-stop orders (see <strong>Managing Stops</strong>).</li>
        <li><strong>Action buttons</strong> — Attach Driver, Change Address, Change Time, Order Info, Cancel.</li>
      </ul>
    </section>

    <!-- Assigning a Driver -->
    <section class="instructions-card">
      <h2 class="instructions-card__title">
        <i class="fa fa-user-plus" aria-hidden="true"></i>
        Assigning a Driver
      </h2>
      <p class="instructions-card__desc">
        There are three ways to assign a driver to a selected order:
      </p>
      <dl class="instructions-dl">
        <dt>Attach Driver button</dt>
        <dd>Opens a list of available free drivers. Search by name, then click the driver to attach.</dd>
        <dt>Right-click the order row</dt>
        <dd>Opens a context menu. Choose <strong>Attach Driver</strong> from the menu.</dd>
        <dt>Select driver on map, then attach</dt>
        <dd>Click a driver marker on the map to open the driver panel, then click <strong>Attach Partner</strong>. The driver is assigned to the currently selected order.</dd>
      </dl>
      <p class="instructions-card__note">
        Only free drivers appear in the attach dialog. A checkmark shows the currently attached driver if the order already has one.
      </p>
    </section>

    <!-- Context Menu -->
    <section class="instructions-card">
      <h2 class="instructions-card__title">
        <i class="fa fa-mouse-pointer" aria-hidden="true"></i>
        Right-Click Menu
      </h2>
      <p class="instructions-card__desc">
        Right-click any order row for quick actions without opening the full detail panel.
      </p>
      <dl class="instructions-dl">
        <dt>Attach Driver</dt>
        <dd>Opens the driver selection dialog.</dd>
        <dt>Change Address</dt>
        <dd>Active orders only. Updates the pickup address.</dd>
        <dt>Cancel</dt>
        <dd>Cancels the order immediately. No confirmation is shown — use with care.</dd>
        <dt>Copy User ID</dt>
        <dd>Cancelled pending orders only. Copies the customer's ID to the clipboard.</dd>
      </dl>
    </section>

    <!-- Managing Stops -->
    <section class="instructions-card">
      <h2 class="instructions-card__title">
        <i class="fa fa-map-pin" aria-hidden="true"></i>
        Managing Stops
      </h2>
      <p class="instructions-card__desc">
        Multi-stop orders show a stop list in the detail panel. You can edit stops on pending orders.
      </p>
      <ul class="instructions-list">
        <li>Use the <strong>up/down arrows</strong> to reorder stops.</li>
        <li>Click the <strong>edit icon</strong> on a stop to change its address, comment, type, or completion status.</li>
        <li>Click the <strong>delete icon</strong> to remove a stop.</li>
        <li>Click <strong>Add Stop</strong> to append a new stop at the end.</li>
        <li>Click <strong>Save Stops</strong> to send all changes to the server.</li>
      </ul>
    </section>

    <!-- Assist -->
    <section class="instructions-card">
      <h2 class="instructions-card__title">
        <i class="fa fa-life-ring" aria-hidden="true"></i>
        Assist
      </h2>
      <p class="instructions-card__desc">
        The <strong>Assist</strong> button in the order detail panel lets you request help from another operator for a difficult order.
      </p>
      <ul class="instructions-list">
        <li>Click <strong>Assist</strong> to send a request. A countdown timer appears while the request is active.</li>
        <li>If another operator responds, the button shows <strong>Assisting: [Name]</strong>.</li>
        <li>The request expires automatically after the countdown reaches zero.</li>
      </ul>
    </section>

    <!-- Preorder Alarm -->
    <section class="instructions-card instructions-card--wide">
      <h2 class="instructions-card__title">
        <i class="fa fa-clock-o" aria-hidden="true"></i>
        Preorder Alarm
      </h2>
      <p class="instructions-card__desc">
        When a preorder is less than 1 hour away from its dispatch time and still has no partner assigned, the system raises an alarm automatically.
      </p>
      <ul class="instructions-list">
        <li>An alarm sound plays (repeats 3 times). If the browser blocked audio before your first interaction, it plays on the next click anywhere on the page.</li>
        <li>A full-screen overlay appears listing all urgent preorders, each showing the route name and minutes remaining until dispatch.</li>
        <li>The overlay <strong>does not close on its own</strong> — you must act on it.</li>
        <li><strong>Go to Preorder</strong> — switches to the Preorder tab so you can assign a partner immediately.</li>
        <li><strong>Dismiss</strong> — closes the overlay. The same preorder will not trigger the alarm again in this session, but new urgent ones will.</li>
      </ul>
    </section>

    <!-- Partner Search -->
    <section class="instructions-card">
      <h2 class="instructions-card__title">
        <i class="fa fa-search" aria-hidden="true"></i>
        Partner Search
      </h2>
      <p class="instructions-card__desc">
        Type a partner's name in the search box at the top of the workflow panel. The order list is replaced by a matching driver list showing name, phone, and online status. Click a driver to select them and view their details on the map.
      </p>
    </section>

    <!-- Negative Balance -->
    <section class="instructions-card">
      <h2 class="instructions-card__title">
        <i class="fa fa-exclamation-circle" aria-hidden="true"></i>
        Negative Balance
      </h2>
      <p class="instructions-card__desc">
        The <strong>Balance</strong> tab lists every partner who owes money. Each row has a red left border and the amount is shown as a bold red pill. Click a row to select that partner and view their location on the map.
      </p>
      <ul class="instructions-list">
        <li>The tab appears automatically as soon as any partner has a negative balance.</li>
        <li>The status dot shows whether the partner is currently free, busy, or offline.</li>
        <li>Balance data refreshes alongside regular driver status updates.</li>
      </ul>
    </section>

    <!-- Map Interactions -->
    <section class="instructions-card">
      <h2 class="instructions-card__title">
        <i class="fa fa-map" aria-hidden="true"></i>
        Map — What You See
      </h2>
      <dl class="instructions-dl">
        <dt>Order circles</dt>
        <dd>Colored dots at pickup locations. Click to select the order and open its detail panel — same as clicking the row in the list.</dd>
        <dt>Driver markers</dt>
        <dd>Icons showing each driver's current position. The selected driver is highlighted and larger. Click to open the driver detail panel.</dd>
        <dt>Blue polyline</dt>
        <dd>The road route from the assigned driver's current location to the order pickup.</dd>
        <dt>Green polyline</dt>
        <dd>The road route from the pickup to the order's destination.</dd>
        <dt>Orange dashed line</dt>
        <dd>Appears when a driver has a destination set. Shows the straight-line path from the driver's current location to where they are heading, with a flag at the destination.</dd>
        <dt>Online users heatmap</dt>
        <dd>Small icons showing where customers currently are (toggle in map filters).</dd>
        <dt>Prediction polygons</dt>
        <dd>Surge pricing zones on the map (toggle in map filters).</dd>
      </dl>
    </section>

    <!-- Map Filters -->
    <section class="instructions-card">
      <h2 class="instructions-card__title">
        <i class="fa fa-filter" aria-hidden="true"></i>
        Map Filters
      </h2>
      <p class="instructions-card__desc">
        The top-left of the map contains filter controls. Changes apply instantly.
      </p>
      <dl class="instructions-dl">
        <dt>Regions</dt>
        <dd>Multi-select. Show only drivers and orders in the selected regions.</dd>
        <dt>Driver Type</dt>
        <dd>Filter the driver markers by vehicle category.</dd>
        <dt>Option</dt>
        <dd>Filter drivers by a specific service option (child seat, wheelchair, etc.).</dd>
        <dt>Status</dt>
        <dd>Filter drivers by status: free, busy, processing, or all.</dd>
        <dt>Heatmap</dt>
        <dd>Choose which order heatmap to show on the map.</dd>
        <dt>Predictions toggle</dt>
        <dd>Show or hide surge pricing zones.</dd>
        <dt>Online Users toggle</dt>
        <dd>Show or hide customer location dots.</dd>
        <dt>Offline Partners toggle</dt>
        <dd>Show or hide offline driver positions.</dd>
      </dl>
    </section>

    <!-- Driver Detail -->
    <section class="instructions-card">
      <h2 class="instructions-card__title">
        <i class="fa fa-id-card" aria-hidden="true"></i>
        Driver Detail Panel
      </h2>
      <p class="instructions-card__desc">
        Click any driver marker on the map to open their panel on the right side.
      </p>
      <ul class="instructions-list">
        <li><strong>Car &amp; plate</strong> — manufacturer, color, license number (click to copy), with a CRM link.</li>
        <li><strong>Phone</strong> — click to copy.</li>
        <li><strong>Balance</strong> — shown in red if negative.</li>
        <li><strong>Stats</strong> — orders in the last 3 days, time since last completed trip, time since last assignment.</li>
        <li><strong>Heading towards</strong> — if the driver has a destination set, their address and estimated travel time are shown. The destination is also drawn as an orange line on the map.</li>
        <li><strong>Options &amp; Tariffs</strong> — which service options and tariff regions the driver is assigned to and whether they are currently active.</li>
        <li><strong>Attach Partner</strong> — assigns this driver to the currently selected order.</li>
      </ul>
    </section>

    <!-- Creating a New Order -->
    <section class="instructions-card">
      <h2 class="instructions-card__title">
        <i class="fa fa-plus-circle" aria-hidden="true"></i>
        Creating a New Order
      </h2>
      <p class="instructions-card__desc">
        When no driver is selected, the right panel shows a new order form.
      </p>
      <ul class="instructions-list">
        <li>Type a pickup address in the autocomplete field, or click anywhere on the map to set the pickup pin directly.</li>
        <li>Add an optional comment.</li>
        <li>Select the order type (car category).</li>
        <li>Click <strong>New Order</strong> to submit.</li>
      </ul>
    </section>

    <!-- Search & Filter Tools -->
    <section class="instructions-card">
      <h2 class="instructions-card__title">
        <i class="fa fa-search" aria-hidden="true"></i>
        Search &amp; Filter Tools
      </h2>
      <dl class="instructions-dl">
        <dt>Order search</dt>
        <dd>The search bar inside the workflow panel filters the current tab's order list by order ID, address, or other fields.</dd>
        <dt>Pending &gt; 1 min</dt>
        <dd>A checkbox in the Pending tab that hides orders placed less than 1 minute ago, so you focus only on orders that have been waiting.</dd>
        <dt>Tracking input</dt>
        <dd>In the header — enter a phone number, address fragment, or comment to highlight matching orders across all tabs. Click the eye icon to start, the red eye to stop.</dd>
        <dt>Regions / Car Types</dt>
        <dd>Multi-select dropdowns in the header that filter both the order list and the map simultaneously.</dd>
        <dt>Phone search</dt>
        <dd>Select a country code on the right side of the header and type a phone number to highlight matching drivers and orders instantly.</dd>
        <dt>Preorder filters</dt>
        <dd>The Preorder and ggAra tabs have a status dropdown and a tariff dropdown to narrow down the list.</dd>
      </dl>
    </section>

    <!-- Audio Notifications -->
    <section class="instructions-card">
      <h2 class="instructions-card__title">
        <i class="fa fa-bell" aria-hidden="true"></i>
        Sound Notifications
      </h2>
      <p class="instructions-card__desc">
        Two sound toggles are in the top-right of the header.
      </p>
      <ul class="instructions-status-list">
        <li>
          <button class="wm-btn-icon wm-btn-icon--active" type="button" style="pointer-events:none;" aria-hidden="true">
            <i class="fa fa-bell" aria-hidden="true"></i>
          </button>
          <strong>Bell — green</strong>: new order sound is ON. A chime plays when a new order arrives.
        </li>
        <li>
          <button class="wm-btn-icon wm-btn-icon--active-warning" type="button" style="pointer-events:none;" aria-hidden="true">
            <i class="fa fa-fire" aria-hidden="true"></i>
          </button>
          <strong>Fire — amber</strong>: cancellation sound is ON. Plays when an order is cancelled.
        </li>
      </ul>
      <p class="instructions-card__note">
        Browsers require at least one click on the page before playing audio. If a sound is toggled but you hear nothing, click anywhere and it will play on the next event.
        The preorder alarm follows the same rule — it plays automatically on your next interaction if it was initially blocked.
      </p>
    </section>

    <!-- Change Time Dialog -->
    <section class="instructions-card">
      <h2 class="instructions-card__title">
        <i class="fa fa-calendar" aria-hidden="true"></i>
        Changing Order Time &amp; Type
      </h2>
      <p class="instructions-card__desc">
        Click <strong>Change Time</strong> in the order detail panel to open the scheduling dialog.
      </p>
      <ul class="instructions-list">
        <li>Set a specific date and time, or use <strong>−15 min / +15 min / Now</strong> for quick adjustments.</li>
        <li>A grid of available car types with prices is shown — select a different type if needed.</li>
        <li>Click <strong>Confirm</strong> to apply, or <strong>Cancel</strong> to discard.</li>
      </ul>
    </section>

    <!-- Real-time Update Schedule -->
    <section class="instructions-card">
      <h2 class="instructions-card__title">
        <i class="fa fa-refresh" aria-hidden="true"></i>
        How Often Data Updates
      </h2>
      <p class="instructions-card__desc">
        Most data arrives instantly via a live connection. The rest is polled on a schedule.
      </p>
      <table class="instructions-table">
        <thead>
          <tr>
            <th>Data</th>
            <th>Interval</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Orders (new &amp; updated)</td>
            <td><span class="wm-chip wm-chip-accent">Instant</span></td>
          </tr>
          <tr>
            <td>Online users on map</td>
            <td><span class="wm-chip wm-chip-success">10 s</span></td>
          </tr>
          <tr>
            <td>Preorder list &amp; alarm check</td>
            <td><span class="wm-chip wm-chip-success">10 s</span></td>
          </tr>
          <tr>
            <td>Driver positions on map</td>
            <td><span class="wm-chip wm-chip-success">20 s</span></td>
          </tr>
          <tr>
            <td>Driver status &amp; counts</td>
            <td><span class="wm-chip wm-chip-info">30 s</span></td>
          </tr>
          <tr>
            <td>Surge pricing zones</td>
            <td><span class="wm-chip wm-chip-warning">3 min</span></td>
          </tr>
        </tbody>
      </table>
      <p class="instructions-card__note">
        The <strong>Refresh</strong> button in the header forces an immediate full reload. There is a 2-minute throttle to prevent accidental spamming.
      </p>
    </section>

  </div>
</div>
